import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { ContextLink } from "@bindery/shared/corpus/document";
import { getPref, setPref } from "@/lib/prefs";
import { useLogger } from "@/logging";
import type { ContextLinkScopeValue } from "./context-link-scope";
import type { ReferenceLookupPorts } from "./reference-lookup";
import {
  ReferenceLookupContext,
  type ReferenceLookupEntry,
} from "./reference-lookup-context";
import { ReferenceLookupDrawer } from "./reference-lookup-drawer";
import { referenceSelection } from "./reference-selection";
import { useReferenceLookup } from "./use-reference-lookup";

/**
 * One lookup per reader surface: the controller, the drawer it drives, and
 * the entry point context links open it through. Owns what the drawer does
 * not: the autoplay preference, the context-link telemetry, and where focus
 * returns when the drawer closes.
 */
export function ReferenceLookupHost({
  scope,
  language,
  ports,
  children,
}: {
  scope: ContextLinkScopeValue;
  language: string;
  ports: ReferenceLookupPorts;
  children: ReactNode;
}) {
  const log = useLogger();
  const [initialAutoplay] = useState(() => getPref("lookupAutoplay", true));
  const logRef = useRef(log);
  useEffect(() => {
    logRef.current = log;
  });
  const {
    state,
    open,
    close,
    select,
    check,
    showDictionary,
    showContext,
    replay,
    retryPronunciations,
    setAutoplay,
  } = useReferenceLookup(ports, initialAutoplay);
  const returnFocus = useRef<HTMLElement | null>(null);
  const readMoreLogged = useRef(false);

  const entry = useMemo<Omit<ReferenceLookupEntry, "activeLinkId">>(() => {
    const openSelection: ReferenceLookupEntry["open"] = (
      selection,
      trigger,
    ) => {
      returnFocus.current = trigger;
      readMoreLogged.current = false;
      open(selection);
      const link = selection.context;
      if (link) {
        logRef.current.event("context_link.open", linkFields(scope, link));
      }
    };
    return {
      open: openSelection,
      openContextLink: (link, model, trigger) => {
        const selection = referenceSelection(
          model,
          link.start,
          link.end,
          language,
        );
        if (selection) openSelection(selection, trigger);
      },
    };
  }, [open, scope, language]);

  const activeLinkId =
    state.status === "open" ? (state.selection.context?.id ?? null) : null;
  const value = useMemo<ReferenceLookupEntry>(
    () => ({ ...entry, activeLinkId }),
    [entry, activeLinkId],
  );

  const onReadMore = (link: ContextLink): void => {
    if (readMoreLogged.current) return;
    readMoreLogged.current = true;
    logRef.current.event("context_link.read_more", {
      ...linkFields(scope, link),
      excerptId: link.excerptId,
      revId: link.attribution.revId,
    });
  };

  return (
    <ReferenceLookupContext value={value}>
      {children}
      <ReferenceLookupDrawer
        state={state}
        onReadMore={onReadMore}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          returnFocus.current?.focus({ preventScroll: true });
          returnFocus.current = null;
        }}
        setAutoplay={(enabled) => {
          setPref("lookupAutoplay", enabled);
          setAutoplay(enabled);
        }}
        close={close}
        select={select}
        check={check}
        showDictionary={showDictionary}
        showContext={showContext}
        replay={replay}
        retryPronunciations={retryPronunciations}
      />
    </ReferenceLookupContext>
  );
}

function linkFields(scope: ContextLinkScopeValue, link: ContextLink) {
  return {
    book: scope.bookSlug,
    chapter: scope.chapterNumber,
    linkId: link.id,
    concept: link.concept,
    kind: link.kind,
  };
}
